<template>
    <div v-if="menu.items.length" :id="CONTEXT_MENU_ID" class="context-menu-wrapper">
        <div
            v-for="(item, idx) in menu.items"
            :key="`context-item-${idx}`"
            :class="{ 'border-bottom': item.isDivider }"
        >
            <button
                v-if="!item.isDivider"
                class="menu-item"
                :class="{ 'd-none': item.hidden }"
                @click="handleItemClick(item)"
            >
                <font-awesome-icon :icon="item.icon" class="mr-2" />
                {{ item.label }}
                <span v-if="item.hint?.length" class="hint">
                    {{ item.hint }}
                </span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onUnmounted, reactive, watch } from 'vue';
import { type ContextMenuItem, type ContextMenu, CONTEXT_MENU_ACTION, CONTEXT_MENU_EVENT, CONTEXT_MENU_ID } from '@/angular-app/libs/fabric/fabric.contextmenu';
import type { CanvasEx } from '@/typings/canvas';
import type { Object as FabricObject } from 'fabric/fabric-impl';

const props = defineProps<{
    canvas: CanvasEx;
    events?: Record<CONTEXT_MENU_ACTION, (element?: FabricObject) => void>
}>();

const menu = reactive({
    x: '0px',
    y: '0px',
    items: [] as ContextMenuItem[],
    element: undefined as FabricObject | undefined
});

const handleItemClick = async (item: ContextMenuItem) => {
    let handler: (() => void | Promise<void>) | undefined;
    if (item.onClick) {
        handler = item.onClick;
    } else if (item.action && props.events?.[item.action]) {
        handler = () => props.events![item.action!](menu.element);
    }

    if (handler) {
        await handler();
    }

    onMenuClosed();
    window.DD_RUM?.addAction(`${item.label} - Right Click Menu`);
};

const onMenuOpened = (event: ContextMenu) => {
    menu.x = `${event.x}px`;
    menu.y = `${event.y}px`;
    menu.items = [...event.items];
    menu.element = event.selectedElement;
};

const onMenuClosed = () => {
    menu.items = [];
};

watch(
    () => props.canvas,
    (canvas) => {
        if (!canvas) return;
        props.canvas.on(CONTEXT_MENU_EVENT.SHOW, onMenuOpened);
        props.canvas.on(CONTEXT_MENU_EVENT.HIDE, onMenuClosed);
    },
    { immediate: true }
);

onUnmounted(() => {
    props.canvas.off(CONTEXT_MENU_EVENT.SHOW, onMenuOpened);
    props.canvas.off(CONTEXT_MENU_EVENT.HIDE, onMenuClosed);
});
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.context-menu-wrapper {
    min-width: 13.125rem;
    background-color: white;
    border: 1px solid get-color('gray-light');
    box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.15);
    position: absolute;
    top: v-bind('menu.y');
    left: v-bind('menu.x');
    border-radius: 0.5rem;
    overflow: hidden;
    z-index: 99;
    .menu-item {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        text-align: left;
        background-color: unset;
        border: unset;
        padding: 0.375rem;
        width: 100%;
        &:hover {
            background-color: get-color('gray-lightest');
        }
        .hint {
            margin-left: auto;
            text-align: center;
            padding: 0.125rem 0.25rem;
            border-radius: 0.125rem;
            background-color: get-color('gray-light');
        }
    }
}
</style>
